<template>
  <div v-if="enabled" v-show="open" class="wisk-ai-chat-panel" :class="{ 'wisk-ai-chat-panel--resizing': resizing, 'wisk-ai-chat-panel--narrow': narrow }" :style="panelStyle">
    <div
      v-if="!narrow"
      class="wisk-ai-chat-panel-resizer"
      role="separator"
      aria-orientation="vertical"
      tabindex="0"
      v-tooltip="'Drag to resize, double click to reset'"
      @mousedown.prevent="startResize"
      @dblclick="setWidth(defaultWidth)"
      @keydown="onResizerKeydown"></div>

    <div class="wisk-ai-chat-panel-header">
      <span>WISK AI</span>
      <button type="button" class="wisk-ai-chat-panel-close" @click="close">×</button>
    </div>

    <WiskAiChat class="wisk-ai-chat-panel-body" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import WiskAiChat from '@/components/aiChat/WiskAiChat'
import { isAiChatEnabled } from '@/modules/aiChat'
import { getPreferences, setPreferences } from '@/modules/storage'

const DEFAULT_WIDTH = 460
const MIN_WIDTH = 320
const MIN_CONTENT_WIDTH = 360
const KEYBOARD_STEP = 20

export default {
  name: 'WiskAiChatWidget',
  components: { WiskAiChat },
  data() {
    return {
      defaultWidth: DEFAULT_WIDTH,
      width: DEFAULT_WIDTH,
      windowWidth: window.innerWidth,
      resizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0
    }
  },
  computed: {
    ...mapGetters(['deviceType']),
    enabled() {
      return isAiChatEnabled(this.$store.state.user)
    },
    open() {
      return this.$store.state.aiChatOpen
    },
    // Below this the panel takes the whole screen instead of sitting next to the content,
    // which keeps it in step with the mobile layout rules in Full.vue.
    narrow() {
      return this.deviceType === 'mobile' || this.windowWidth < MIN_WIDTH + MIN_CONTENT_WIDTH
    },
    maxWidth() {
      return Math.max(MIN_WIDTH, this.windowWidth - MIN_CONTENT_WIDTH)
    },
    panelStyle() {
      return this.narrow ? {} : { width: `${this.width}px` }
    }
  },
  methods: {
    close() {
      this.$store.commit('mutateAiChatOpen', false)
    },
    setWidth(value, persist = true) {
      this.width = Math.round(Math.min(Math.max(value, MIN_WIDTH), this.maxWidth))
      this.applyContentOffset()

      if (persist) {
        setPreferences('aiChat.panelWidth', this.width)
      }
    },
    applyContentOffset() {
      const offset = this.open && !this.narrow ? `${this.width}px` : '0px'
      document.documentElement.style.setProperty('--wisk-ai-chat-offset', offset)
    },
    startResize(event) {
      this.resizing = true
      this.resizeStartX = event.clientX
      this.resizeStartWidth = this.width
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
    },
    onResize(event) {
      this.setWidth(this.resizeStartWidth + (this.resizeStartX - event.clientX), false)
    },
    stopResize() {
      if (!this.resizing) return

      this.resizing = false
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
      setPreferences('aiChat.panelWidth', this.width)
    },
    onResizerKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.setWidth(this.width + KEYBOARD_STEP)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.setWidth(this.width - KEYBOARD_STEP)
      }
    },
    onWindowResize() {
      this.windowWidth = window.innerWidth
      this.setWidth(this.width, false)
    }
  },
  mounted() {
    this.setWidth(Number(getPreferences('aiChat.panelWidth', DEFAULT_WIDTH)) || DEFAULT_WIDTH, false)
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeUnmount() {
    this.stopResize()
    window.removeEventListener('resize', this.onWindowResize)
    document.documentElement.style.setProperty('--wisk-ai-chat-offset', '0px')
  },
  watch: {
    open() {
      this.applyContentOffset()
    },
    narrow() {
      this.applyContentOffset()
    }
  }
}
</script>

<style lang="scss" scoped>
.wisk-ai-chat-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  background: var(--wisk-background, #fff);
  border-left: 1px solid var(--wisk-border, #e0e0e6);
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.08);

  &--resizing {
    user-select: none;
  }

  &--narrow {
    left: 0;
  }
}

.dark-mode .wisk-ai-chat-panel {
  --wisk-background: var(--darkreader-neutral-background, #181a1b);
  --wisk-background-alt: #26292b;
  --wisk-border: #3a3e41;
  --wisk-code-background: rgba(255, 255, 255, 0.08);
  color: var(--darkreader-neutral-text, #e8e6e3);
}

.wisk-ai-chat-panel-resizer {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;
  width: 7px;
  cursor: col-resize;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 3px;
    width: 1px;
    background: transparent;
  }

  &:hover::after,
  &:focus::after {
    background: var(--wisk-primary, #2f6fed);
  }

  &:focus {
    outline: none;
  }
}

.wisk-ai-chat-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-weight: 600;
  border-bottom: 1px solid var(--wisk-border, #e0e0e6);
}

.wisk-ai-chat-panel-close {
  border: none;
  background: transparent;
  color: inherit;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.wisk-ai-chat-panel-body {
  flex: 1;
  min-height: 0;
}
</style>
